import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { TableBody, TableCell, TableContainer, TableHead } from "@mui/material";

import { useCurrentDate } from "../../../../../store/current-date/current-date.selectors";
import { useFreeProxyData } from "../../../../../store/freeProxy/freeProxy.selectors";

import { getTimeDifferenceInMinutes } from "../../../../../utils/helpers";
import {
  formatTime,
  toStringGoogle,
  transformProxyType,
} from "../../../../../utils/helpers/formatTableDataFilter";
import { getTranslatedCity } from "../../../../../utils/helpers/getTranslatedCity";

import { useGetPositionOfDropDown } from "../../../../../hooks";

import { BoxWrapper, ButtonCopy, Container } from "../../../../../components";
import Tooltip from "../../../../../components/common/Tooltip";
import CircleIconFlag from "../../../../../components/ui/CircleIconFlag";
import { IconSprite } from "../../../../../components/ui/IconSprite/IconSprite";
import { TransitionBox } from "../../../../../components/ui/TransitionBox/TransitionBox";
import { Pagination } from "../Pagination/Pagination";
import { addStyles } from "./addStyles";
import CountriesDropDown from "./components/CountriesDropDown";
import HeadTableCell from "./components/HeadTableCell";

import {
  ChipStyled,
  CountryButton,
  CountryLink,
  Icon,
  IpWrapper,
  NoDataText,
  TableCellStyled,
  TableRowOne,
  TableRowTwo,
  TableStyled,
  Text,
  TextWrapper,
} from "./Table.styled";

export const Table = () => {
  const [isCountriesDropDownOpen, setIsCountriesDropDownOpen] = useState(false);

  const { freeProxies, paginationData } = useFreeProxyData();
  const currentDate = useCurrentDate();

  //  translation for countries
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const translatedFreeProxies = freeProxies?.map(el => {
    let country;
    try {
      let regionNames = new Intl.DisplayNames([currentLanguage], {
        type: "region",
      });
      country = regionNames.of(el?.countryIsoCode);
    } catch {
      country = el?.country;
    }

    return { ...el, country };
  });
  //  translation for countries

  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const tableRef = useRef(null);
  const selectId = useId();

  const countriesDropDownHandler = e => {
    setIsCountriesDropDownOpen(prevState => !prevState);
  };

  const handleClickOutside = event => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsCountriesDropDownOpen(false);
    }
  };

  const handleKeyDown = event => {
    if (event.key === "Escape") {
      setIsCountriesDropDownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const { positionLeft } = useGetPositionOfDropDown(
    dropdownRef,
    tableRef,
    isCountriesDropDownOpen,
  );

  return (
    <Container>
      <BoxWrapper addStyles={addStyles.boxWrapper}>
        <TableContainer>
          <TableStyled aria-label="simple table" ref={tableRef}>
            <TableHead>
              <TableRowOne>
                <HeadTableCell>IP</HeadTableCell>

                <HeadTableCell>{t("freeProxyServer.table.port")}</HeadTableCell>

                <TableCell ref={dropdownRef}>
                  <CountryButton onClick={countriesDropDownHandler}>
                    {t("freeProxyServer.table.country")}
                    <IconSprite tag="filter" width="17" height="16" />
                  </CountryButton>
                  <TransitionBox isOpen={isCountriesDropDownOpen}>
                    <CountriesDropDown
                      top={"100px"}
                      topMobile={"68px"}
                      left={positionLeft}
                      closeSelect={countriesDropDownHandler}
                      // selectedValue={selectedCountry}
                      // handler={handleSelectedCountry}
                      selectId={selectId}
                    />
                  </TransitionBox>
                </TableCell>

                <HeadTableCell>{t("freeProxyServer.table.city")}</HeadTableCell>

                <HeadTableCell>{t("freeProxyServer.table.type")}</HeadTableCell>

                <HeadTableCell>
                  {t("freeProxyServer.table.anonymity")}
                </HeadTableCell>

                <HeadTableCell>Google</HeadTableCell>

                <HeadTableCell>
                  {t("freeProxyServer.table.speed")}
                </HeadTableCell>

                <HeadTableCell>
                  {t("freeProxyServer.table.updated")}
                </HeadTableCell>
              </TableRowOne>
            </TableHead>

            <TableBody>
              {!!freeProxies.length &&
                translatedFreeProxies?.map(
                  ({
                    id,
                    ip,
                    port,
                    country,
                    city,
                    type,
                    anonymity,
                    checkedSites,
                    delayMS,
                    countryIsoCode,
                    lastCheck,
                  }) => (
                    <TableRowTwo key={id}>
                      <TableCell>
                        <IpWrapper>
                          {ip}
                          <ButtonCopy
                            text={ip}
                            addStyles={addStyles.buttonCopy}
                          />
                        </IpWrapper>
                      </TableCell>

                      <TableCell>{port}</TableCell>

                      <TableCell>
                        <CountryLink>
                          <CircleIconFlag
                            className={countryIsoCode.toLowerCase()}
                          />
                          <Tooltip id={`country-${id}`} content={country}>
                            {country}
                          </Tooltip>
                        </CountryLink>
                      </TableCell>

                      <TableCell>
                        <Tooltip
                          id={`city-${id}`}
                          content={getTranslatedCity(city, currentLanguage)}
                        >
                          {getTranslatedCity(city, currentLanguage)}
                        </Tooltip>
                      </TableCell>

                      <TableCell>{type}</TableCell>

                      <TableCellStyled>
                        <ChipStyled label={transformProxyType(anonymity)} />
                      </TableCellStyled>

                      <TableCellStyled>
                        <ChipStyled
                          label={toStringGoogle(checkedSites?.Google, t)}
                          color="primary"
                        />
                      </TableCellStyled>

                      <TableCellStyled>
                        <ChipStyled
                          label={formatTime(delayMS, t)}
                          color="primary"
                        />
                      </TableCellStyled>

                      <TableCell>
                        {getTimeDifferenceInMinutes(lastCheck, currentDate) +
                          " " +
                          t("global.updateProxy")}
                      </TableCell>
                    </TableRowTwo>
                  ),
                )}
            </TableBody>
          </TableStyled>
        </TableContainer>

        {!paginationData || paginationData?.totalPages === 0 ? (
          <NoDataText>{t("freeProxyServer.noData")}</NoDataText>
        ) : (
          <>
            <Pagination />

            <TextWrapper>
              <Icon>
                <IconSprite tag="info" width="20" height="20" />
              </Icon>

              <Text>{t("freeProxyServer.infoText")}</Text>
            </TextWrapper>
          </>
        )}
      </BoxWrapper>
    </Container>
  );
};
